import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setGlobalAudioPlaying, setGlobalAudioSource } from 'store/actions';
import { useLocation } from 'react-router-dom';

const AUDIO_MAP = {
  '/tong-quan': '/assets/audio/sound1.mp3',
  '/lien-ket-vung': '/assets/audio/sound2.mp3',
  '/tien-ich': '/assets/audio/sound3.mp3',
  '/mat-bang-tang': '/assets/audio/sound1.mp3',
  '/can-ho-mau': '/assets/audio/sound2.mp3',
  '/so-do-tong-the': '/assets/audio/sound1.mp3',
  '/so-do-tien-ich': '/assets/audio/sound2.mp3',
  '/thu-vien': '/assets/audio/sound1.mp3',
  // Add more routes as needed
};

export const GlobalAudio = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const audioRef = useRef(null);
  const { isPlaying, source } = useSelector((state) => state.globalAudio);
  const [hasInteracted, setHasInteracted] = useState(false);
  const fadeIntervalRef = useRef(null);

  // Helper function to fade in volume gradually
  const fadeInVolume = () => {
    if (audioRef.current && fadeIntervalRef.current === null) {
      audioRef.current.volume = 0;
      let currentVolume = 0;
      const targetVolume = 0.2;
      const fadeStep = 0.01;
      const fadeSpeed = 200; // milliseconds

      fadeIntervalRef.current = setInterval(() => {
        currentVolume += fadeStep;
        if (currentVolume >= targetVolume) {
          currentVolume = targetVolume;
          clearInterval(fadeIntervalRef.current);
          fadeIntervalRef.current = null;
        }
        audioRef.current.volume = currentVolume;
      }, fadeSpeed);
    }
  };

  // Helper function to stop fade in
  const stopFadeIn = () => {
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  };

  // Update audio source based on pathname
  useEffect(() => {
    const pathname = location.pathname;
    const newSource = AUDIO_MAP[pathname] || '/assets/audio/sound1.mp3';

    if (newSource !== source) {
      dispatch(setGlobalAudioSource(newSource));
    }
  }, [location.pathname, source, dispatch]);

  // Auto-reload and play when source changes
  useEffect(() => {
    if (audioRef.current && hasInteracted) {
      stopFadeIn(); // Stop any ongoing fade
      audioRef.current.load();
      if (isPlaying) {
        audioRef.current
          .play()
          .then(() => {
            fadeInVolume(); // Start fade in after play begins
          })
          .catch((error) => {
            console.log('Audio play failed:', error);
          });
      }
    }
  }, [source, isPlaying, hasInteracted]);

  // Handle first user interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!hasInteracted && audioRef.current) {
        audioRef.current
          .play()
          .then(() => {
            fadeInVolume(); // Start fade in after play begins
          })
          .catch((error) => {
            console.log('Audio autoplay failed:', error);
          });
        setHasInteracted(true);
        dispatch(setGlobalAudioPlaying(true));
      }
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, {
      once: true,
    });
    document.addEventListener('keydown', handleFirstInteraction, {
      once: true,
    });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [hasInteracted, dispatch]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      stopFadeIn();
    };
  }, []);

  // Sync audio playback with Redux state
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current
          .play()
          .then(() => {
            fadeInVolume(); // Start fade in after play begins
          })
          .catch((error) => {
            console.log('Audio play failed:', error);
          });
      } else {
        stopFadeIn(); // Stop fade when pausing
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  return (
    <audio loop ref={audioRef} key={source}>
      <source src={source} type="audio/mp3" />
    </audio>
  );
};
